import { jsx, jsxs } from "react/jsx-runtime";
import { useRef, useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../../../lib/utils.js";
import { Hls } from "../../../node_modules/.pnpm/hls.js@1.6.15/node_modules/hls.js/dist/hls.js";
import Loader2 from "../../../node_modules/.pnpm/lucide-react@0.309.0_react@18.3.1/node_modules/lucide-react/dist/esm/icons/loader-2.js";
function QuizVideoPlayer({
  src,
  mimeType,
  isHls = false,
  poster,
  className,
  onReady,
  onError
}) {
  const { t } = useTranslation("quiz");
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const isSafari = useMemo(() => {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent;
    return /^((?!chrome|android).)*safari/i.test(ua) || ua.includes("Mac") && ua.includes("Safari") && !ua.includes("Chrome");
  }, []);
  const isHlsSource = useMemo(() => {
    if (isHls) return true;
    if (mimeType === "application/x-mpegURL" || mimeType === "application/vnd.apple.mpegurl") return true;
    if (src.includes(".m3u8")) return true;
    return false;
  }, [isHls, mimeType, src]);
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return;
    const handleCanPlay = () => {
      setIsLoading(false);
      onReady == null ? void 0 : onReady();
    };
    const handleError = () => {
      setIsLoading(false);
      setHasError(true);
      onError == null ? void 0 : onError(t("shared.cannotLoadVideo"));
    };
    const handleWaiting = () => {
      setIsLoading(true);
    };
    const handlePlaying = () => {
      setIsLoading(false);
    };
    video.addEventListener("canplay", handleCanPlay);
    video.addEventListener("error", handleError);
    video.addEventListener("waiting", handleWaiting);
    video.addEventListener("playing", handlePlaying);
    if (isHlsSource) {
      if (video.canPlayType("application/vnd.apple.mpegurl") || isSafari) {
        video.src = src;
      } else if (Hls.isSupported()) {
        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: false
        });
        hls.loadSource(src);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          setIsLoading(false);
          onReady == null ? void 0 : onReady();
        });
        hls.on(Hls.Events.ERROR, (_, data) => {
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                hls.startLoad();
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                hls.recoverMediaError();
                break;
              default:
                setHasError(true);
                onError == null ? void 0 : onError(t("shared.cannotLoadVideo"));
                hls.destroy();
                break;
            }
          }
        });
        hlsRef.current = hls;
      } else {
        setHasError(true);
        onError == null ? void 0 : onError(t("shared.cannotLoadVideo"));
      }
    } else {
      video.src = src;
    }
    return () => {
      video.removeEventListener("canplay", handleCanPlay);
      video.removeEventListener("error", handleError);
      video.removeEventListener("waiting", handleWaiting);
      video.removeEventListener("playing", handlePlaying);
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [src, isHlsSource, isSafari, onReady, onError]);
  if (hasError) {
    return /* @__PURE__ */ jsx("div", { className: cn("flex items-center justify-center py-8 bg-gray-100 rounded-xl", className), children: /* @__PURE__ */ jsx("p", { className: "text-gray-500 text-sm", children: t("shared.cannotLoadVideo") }) });
  }
  return /* @__PURE__ */ jsxs("div", { className: cn("relative", className), children: [
    isLoading && /* @__PURE__ */ jsx("div", { className: "absolute inset-0 flex items-center justify-center bg-gray-100 rounded-xl z-10", children: /* @__PURE__ */ jsx(Loader2, { className: "w-6 h-6 animate-spin text-gray-400" }) }),
    /* @__PURE__ */ jsx(
      "video",
      {
        ref: videoRef,
        className: "w-full rounded-xl",
        controls: true,
        playsInline: true,
        poster,
        preload: "auto"
      }
    )
  ] });
}
export {
  QuizVideoPlayer
};
//# sourceMappingURL=quiz-video-player.js.map
